// ไฟล์: frontend/src/pages/teacher/Attendance.js
import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Footer from "../../components/Footer";
import api from "../../services/api";
import Swal from "sweetalert2";
import { DEFAULT_AVATAR } from "../../assets/default_avatar";

function TeacherAttendance() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // สถานะการเข้าเรียนที่เป็นไปได้
  const attendanceStatuses = [
    { value: "present", label: "มาเรียน", color: "success" },
    { value: "absent", label: "ขาดเรียน", color: "danger" },
    { value: "sick_leave", label: "ลาป่วย", color: "info" },
    { value: "personal_leave", label: "ลากิจ", color: "secondary" },
    { value: "late", label: "มาสาย", color: "warning" },
  ];

  useEffect(() => {
    // เรียกข้อมูลชั้นเรียนที่ครูรับผิดชอบ
    const fetchClasses = async () => {
      try {
        const response = await api.class.getMyClasses();
        const myClasses = response.data.data || [];
        setClasses(myClasses);

        // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
        if (myClasses.length > 0) {
          setSelectedClassId(myClasses[0].id);
        }
      } catch (error) {
        console.error("Error fetching classes:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถโหลดข้อมูลชั้นเรียนได้",
          confirmButtonColor: "#ff6b01",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchClasses();
  }, []);

  useEffect(() => {
    // เรียกข้อมูลนักเรียนและการเข้าเรียนตามชั้นเรียนและวันที่ที่เลือก
    const fetchStudentsAttendance = async () => {
      if (!selectedClassId || !selectedDate) return;

      try {
        setIsLoading(true);
        const response = await api.attendance.getAttendanceByClassAndDate(
          selectedClassId,
          selectedDate
        );
        setStudents(response.data.data || []);
      } catch (error) {
        console.error("Error fetching students attendance:", error);
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: "ไม่สามารถโหลดข้อมูลการเข้าเรียนได้",
          confirmButtonColor: "#ff6b01",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudentsAttendance();
  }, [selectedClassId, selectedDate]);

  // บันทึกสถานะการเข้าเรียน
  const handleAttendanceChange = async (studentId, status) => {
    try {
      await api.attendance.recordAttendance({
        student_id: studentId,
        class_id: selectedClassId,
        attendance_date: selectedDate,
        status: status,
        note: "",
      });

      // อัปเดตสถานะในรายการนักเรียน
      setStudents((prevStudents) =>
        prevStudents.map((student) =>
          student.student_id === studentId
            ? { ...student, status: status }
            : student
        )
      );

      // แสดงข้อความสำเร็จเล็กๆ ด้านมุมขวาบน
      const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });

      Toast.fire({
        icon: "success",
        title: "บันทึกสถานะเรียบร้อย",
      });
    } catch (error) {
      console.error("Error recording attendance:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกการเข้าเรียนได้",
        confirmButtonColor: "#ff6b01",
      });
    }
  };

  // บันทึกสถานะการเข้าเรียนทั้งหมด
  const handleMarkAll = async (status) => {
    try {
      setIsLoading(true);

      // ยืนยันการบันทึกทั้งหมด
      const result = await Swal.fire({
        title: "ยืนยันการบันทึก",
        text: `ยืนยันการบันทึกสถานะ "${
          attendanceStatuses.find((s) => s.value === status)?.label
        }" สำหรับนักเรียนทั้งหมด?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#ff6b01",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "ยืนยัน",
        cancelButtonText: "ยกเลิก",
      });

      if (result.isConfirmed) {
        // บันทึกสถานะสำหรับนักเรียนทุกคน
        const promises = students.map((student) =>
          api.attendance.recordAttendance({
            student_id: student.student_id,
            class_id: selectedClassId,
            attendance_date: selectedDate,
            status: status,
            note: "",
          })
        );

        await Promise.all(promises);

        // อัปเดตสถานะในรายการนักเรียน
        setStudents((prevStudents) =>
          prevStudents.map((student) => ({ ...student, status: status }))
        );

        Swal.fire({
          icon: "success",
          title: "บันทึกสำเร็จ",
          text: "บันทึกสถานะการเข้าเรียนทั้งหมดเรียบร้อยแล้ว",
          confirmButtonColor: "#ff6b01",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error marking all attendance:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกการเข้าเรียนทั้งหมดได้",
        confirmButtonColor: "#ff6b01",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // สรุปจำนวนสถานะการเข้าเรียน
  const getAttendanceSummary = () => {
    const summary = {
      present: 0,
      absent: 0,
      sick_leave: 0,
      personal_leave: 0,
      late: 0,
      not_recorded: 0,
    };

    students.forEach((student) => {
      if (!student.status) {
        summary.not_recorded++;
      } else {
        summary[student.status]++;
      }
    });

    return summary;
  };

  const attendanceSummary = getAttendanceSummary();

  return (
    <div className="wrapper">
      <Header />
      <Sidebar />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">บันทึกการเข้าเรียน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">บันทึกการเข้าเรียน</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกชั้นเรียนและวันที่ */}
                <div className="row">
                  <div className="col-12">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">เลือกชั้นเรียนและวันที่</h3>
                      </div>
                      <div className="card-body">
                        {classes.length === 0 ? (
                          <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ
                            กรุณาติดต่อผู้ดูแลระบบ
                          </div>
                        ) : (
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>ชั้นเรียน</label>
                                <select
                                  className="form-control"
                                  value={selectedClassId}
                                  onChange={(e) =>
                                    setSelectedClassId(e.target.value)
                                  }
                                >
                                  {classes.map((classItem) => (
                                    <option
                                      key={classItem.id}
                                      value={classItem.id}
                                    >
                                      {classItem.class_name} (ห้อง{" "}
                                      {classItem.room_number})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>วันที่</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={selectedDate}
                                  onChange={(e) =>
                                    setSelectedDate(e.target.value)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* รายการนักเรียนและการเข้าเรียน */}
                {classes.length > 0 && (
                  <div className="row">
                    <div className="col-12">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">
                            รายการนักเรียนและการเข้าเรียน
                          </h3>
                          <div className="card-tools">
                            <button
                              type="button"
                              className="btn btn-tool"
                              data-card-widget="collapse"
                            >
                              <i className="fas fa-minus"></i>
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          {/* สรุปการเข้าเรียน */}
                          <div className="row">
                            <div className="col-12 col-md-2">
                              <div className="info-box bg-success">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">มาเรียน</span>
                                  <span className="info-box-number">
                                    {attendanceSummary.present}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-2">
                              <div className="info-box bg-danger">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">
                                    ขาดเรียน
                                  </span>
                                  <span className="info-box-number">
                                    {attendanceSummary.absent}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-2">
                              <div className="info-box bg-info">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">ลาป่วย</span>
                                  <span className="info-box-number">
                                    {attendanceSummary.sick_leave}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-2">
                              <div className="info-box bg-secondary">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">ลากิจ</span>
                                  <span className="info-box-number">
                                    {attendanceSummary.personal_leave}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-2">
                              <div className="info-box bg-warning">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">มาสาย</span>
                                  <span className="info-box-number">
                                    {attendanceSummary.late}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-2">
                              <div className="info-box bg-light">
                                <div className="info-box-content text-center">
                                  <span className="info-box-text">
                                    ยังไม่บันทึก
                                  </span>
                                  <span className="info-box-number">
                                    {attendanceSummary.not_recorded}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* ปุ่มบันทึกทั้งหมด */}
                          
                          <div className="row mb-3">
                            <div className="col-12">
                              <div className="dropdown">
                                <button
                                  className="btn btn-secondary dropdown-toggle"
                                  type="button"
                                  id="markAllDropdown"
                                  data-bs-toggle="dropdown"
                                  aria-expanded="false"
                                >
                                  บันทึกทั้งหมดเป็น...
                                </button>
                                <ul
                                  className="dropdown-menu"
                                  aria-labelledby="markAllDropdown"
                                >
                                  {attendanceStatuses.map((status) => (
                                    <li key={status.value}>
                                      <a
                                        className={`dropdown-item text-${status.color}`}
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleMarkAll(status.value);
                                        }}
                                      >
                                        <i
                                          className={`fas fa-circle me-2 text-${status.color}`}
                                        ></i>
                                        {status.label}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                          {/* รายการนักเรียน */}
                          {students.length === 0 ? (
                            <div className="alert alert-info">
                              <i className="fas fa-info-circle mr-2"></i>
                              ไม่พบข้อมูลนักเรียนในชั้นเรียนนี้
                            </div>
                          ) : (
                            <div className="row">
                              {students.map((student) => (
                                <div
                                  key={student.student_id}
                                  className="col-md-4 col-sm-6 col-12"
                                >
                                  <div className="info-box">
                                    <img
                                      className="img-circle mr-3"
                                      src={`https://cms2568.backend.kruwirat.com/uploads/${student.profile_pic}`}
                                      alt={`${student.first_name} ${student.last_name}`}
                                      style={{
                                        width: "70px",
                                        height: "70px",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = DEFAULT_AVATAR;
                                      }}
                                    />
                                    <div className="info-box-content">
                                      <span className="info-box-text">
                                        {student.first_name} {student.last_name}
                                      </span>
                                      <span className="info-box-number">
                                        {student.student_code}
                                      </span>

                                      <div className="btn-group mt-2">
                                        {attendanceStatuses.map((status) => (
                                          <button
                                            key={status.value}
                                            className={`btn btn-sm ${
                                              student.status === status.value
                                                ? `btn-${status.color}`
                                                : `btn-outline-${status.color}`
                                            }`}
                                            onClick={() =>
                                              handleAttendanceChange(
                                                student.student_id,
                                                status.value
                                              )
                                            }
                                          >
                                            {status.label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default TeacherAttendance;
